import { getClientUserByAuthId, touchLastLogin } from '@infosaas/shared'
import { create } from 'zustand'
import { supabase } from '../lib/supabase'

const APP_URL =
  import.meta.env.VITE_APP_URL || (typeof window !== 'undefined' ? window.location.origin : '')

/**
 * Store de autenticação.
 * O listener `onAuthStateChange` NÃO é montado aqui.
 * Quem monta é o componente `<AuthInit />` em `shared/auth/AuthInit.jsx`,
 * uma única vez no topo da árvore.
 */
export const useAuthStore = create((set, get) => ({
  session: null,
  profile: null,
  loading: true,

  setSession: (session) => set({ session }),
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),

  fetchProfile: async (tentativa = 1) => {
    const { session, profile: previousProfile } = get()
    if (!session?.user) {
      set({ profile: null, loading: false })
      return
    }

    // Timeout defensivo: se a query do profile pendurar (RLS, rede), libera o
    // boot ao inves de deixar o spinner eterno.
    const PROFILE_TIMEOUT_MS = 8000
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('fetchProfile timeout')), PROFILE_TIMEOUT_MS),
    )

    try {
      const profile = await Promise.race([
        getClientUserByAuthId(supabase, session.user.id),
        timeout,
      ])
      if (profile) {
        touchLastLogin(supabase, profile.id).catch(() => {})
        set({ profile })
      } else if (!previousProfile) {
        // Banco realmente confirmou "nao tem client_user pra esse auth.uid".
        // Soh marca null se ainda nao tinhamos um carregado. Evita lampejo
        // de "Acesso nao configurado" entre refreshes de sessao.
        set({ profile: null })
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('fetchProfile error:', err)
      // Timeout ou rede nao provam que a conta nao tem acesso: tenta de novo
      // uma vez antes de mostrar "Acesso nao configurado".
      if (!previousProfile && tentativa < 2) {
        return get().fetchProfile(tentativa + 1)
      }
      // Falha de rede/timeout: nao nulifica o profile carregado. UI continua
      // funcionando com o estado anterior.
      if (!previousProfile) set({ profile: null })
    }
    set({ loading: false })
  },

  sendMagicLink: async (email) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: APP_URL,
      },
    })
    if (error) throw error
  },

  signOut: async () => {
    await supabase.auth.signOut()
    set({ session: null, profile: null })
  },
}))

// Dev-only: login com senha. Importado apenas no MagicLinkLogin sob flag DEV.
// Vite remove a chamada na build de produção via dead-code elimination.
export async function devSignInWithPassword(email, password) {
  if (!import.meta.env.DEV) {
    throw new Error('Password login só está disponível em desenvolvimento')
  }
  const { error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
}
